<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <h1 class="h4 mb-3">Sales Queue</h1>

              <!-- Summary stats -->
              <div class="row mb-3 g-3">
                <div class="col-md-4">
                  <div class="card bg-white border">
                    <div class="card-body py-2 text-center">
                      <div class="text-muted small">Ready to Sell</div>
                      <div class="h5 mb-0">{{ readyCount }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card bg-white border">
                    <div class="card-body py-2 text-center">
                      <div class="text-muted small">Listed on eBay</div>
                      <div class="h5 mb-0">{{ listedCount }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card bg-white border">
                    <div class="card-body py-2 text-center">
                      <div class="text-muted small">Total Listing Value</div>
                      <div class="h5 mb-0">${{ totalListingValue }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <!-- Error -->
              <div v-else-if="error" class="alert alert-danger py-2">{{ error }}</div>

              <!-- Empty state -->
              <div v-else-if="!filteredOrders.length" class="text-muted text-center py-4">
                No orders in the sales queue.
              </div>

              <!-- Orders table -->
              <div v-else class="table-responsive">
                <table class="table table-sm table-hover align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>#</th>
                      <th>Serial</th>
                      <th>Device</th>
                      <th>Status</th>
                      <th>Listing Price</th>
                      <th>eBay Listing</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="order in filteredOrders" :key="order.order_id">
                      <!-- Main row -->
                      <tr
                        :class="{ 'table-success': order.order_status === 'SOLD' }"
                        style="cursor: pointer;"
                        @click="toggleExpand(order.order_id)"
                      >
                        <td class="text-center">
                          <span class="small">{{ expandedId === order.order_id ? '&#9660;' : '&#9654;' }}</span>
                        </td>
                        <td>
                          <router-link :to="'/orders/' + order.order_id" @click.stop>
                            {{ order.order_id }}
                          </router-link>
                        </td>
                        <td class="small">{{ order.serial_number }}</td>
                        <td>
                          <span>{{ order.device_make }} {{ order.device_model }}</span>
                          <br />
                          <small class="text-muted">{{ order.device_commonname }}</small>
                        </td>
                        <td>
                          <span class="badge" :class="statusBadge(order)">{{ order.order_status }}</span>
                        </td>
                        <td>
                          <span v-if="order.sale_price">${{ order.sale_price }}</span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                          <a
                            v-if="order.ebay_listing_url"
                            :href="order.ebay_listing_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-decoration-none"
                            @click.stop
                          >
                            View Listing
                            <span v-if="order.ebay_listing_status" class="badge ms-1" :class="{
                              'bg-success': order.ebay_listing_status === 'ACTIVE',
                              'bg-dark': order.ebay_listing_status === 'SOLD',
                              'bg-secondary': order.ebay_listing_status === 'ENDED',
                            }">{{ order.ebay_listing_status }}</span>
                          </a>
                          <span v-else class="text-muted small">Not listed</span>
                        </td>
                        <td @click.stop>
                          <div class="d-flex gap-1">
                            <button
                              v-if="order.order_status === 'COMPLETE'"
                              class="btn btn-outline-dark btn-sm"
                              @click="openProcessSale(order)"
                            >
                              Process Sale
                            </button>
                            <button
                              class="btn btn-outline-success btn-sm"
                              @click="openListingGenerator(order)"
                            >
                              Generate Listing
                            </button>
                          </div>
                        </td>
                      </tr>

                      <!-- Expanded shipping row -->
                      <tr v-if="expandedId === order.order_id">
                        <td colspan="8" class="bg-light px-4 py-3">
                          <div class="row g-2 align-items-end">
                            <div class="col-md-3">
                              <label class="form-label small mb-0">Tracking #</label>
                              <input
                                v-model="shippingForms[order.order_id].tracking_number"
                                type="text"
                                class="form-control form-control-sm"
                              />
                            </div>
                            <div class="col-md-2">
                              <label class="form-label small mb-0">Carrier</label>
                              <select v-model="shippingForms[order.order_id].shipping_carrier" class="form-select form-select-sm">
                                <option value="">None</option>
                                <option value="UPS">UPS</option>
                                <option value="FEDEX">FedEx</option>
                                <option value="USPS">USPS</option>
                                <option value="DHL">DHL</option>
                                <option value="OTHER">Other</option>
                              </select>
                            </div>
                            <div class="col-md-2">
                              <label class="form-label small mb-0">Ship Date</label>
                              <input
                                v-model="shippingForms[order.order_id].ship_date"
                                type="date"
                                class="form-control form-control-sm"
                              />
                            </div>
                            <div class="col-md-2">
                              <label class="form-label small mb-0">Status</label>
                              <select v-model="shippingForms[order.order_id].shipping_status" class="form-select form-select-sm">
                                <option value="">None</option>
                                <option value="LABEL_CREATED">Label Created</option>
                                <option value="IN_TRANSIT">In Transit</option>
                                <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="EXCEPTION">Exception</option>
                              </select>
                            </div>
                            <div class="col-md-2 d-flex align-items-end">
                              <button
                                class="btn btn-outline-primary btn-sm"
                                @click="saveShipping(order.order_id)"
                                :disabled="savingShippingId === order.order_id"
                              >
                                <span v-if="savingShippingId === order.order_id" class="spinner-border spinner-border-sm me-1"></span>
                                Save
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Process Sale Modal (reuses InputModal) -->
    <InputModal
      :visible="saleModal.visible"
      title="Process Sale"
      message="Enter the eBay order ID to import sale data for this device."
      input-type="text"
      input-label="eBay Order ID"
      placeholder="12-34567-89012"
      :required="true"
      confirm-text="Import Sale"
      @confirm="onSaleConfirm"
      @cancel="saleModal.visible = false"
    />

    <!-- Listing Generator Modal -->
    <EbayListingModal
      :visible="listingModal.visible"
      :order="listingModal.order"
      @close="listingModal.visible = false"
    />
  </div>
</template>

<script>
import { APIService } from "../http/APIService";
import InputModal from "./InputModal.vue";
import EbayListingModal from "./EbayListingModal.vue";

export default {
  name: "SalesQueue",
  components: { InputModal, EbayListingModal },
  data() {
    return {
      orders: [],
      loading: false,
      error: null,
      api: new APIService(),
      expandedId: null,
      shippingForms: {},
      savingShippingId: null,
      saleModal: {
        visible: false,
        order: null,
      },
      listingModal: {
        visible: false,
        order: null,
      },
    };
  },
  created() {
    if (!this.isSupervisor) {
      this.$router.replace("/dashboard");
      return;
    }
    this.fetchOrders();
  },
  computed: {
    isSupervisor() {
      try {
        const raw = localStorage.getItem("currentUser");
        if (!raw) return false;
        const user = JSON.parse(raw);
        const role = (user.role || "").toLowerCase();
        return role === "supervisor" || role === "admin";
      } catch {
        return false;
      }
    },
    filteredOrders() {
      return this.orders
        .filter((o) => {
          if (o.order_status === "COMPLETE") return true;
          if (o.ebay_listing_url && o.order_status !== "CANCELLED") return true;
          return false;
        })
        .sort((a, b) => {
          // Orders with eBay listings first
          const aListed = a.ebay_listing_url ? 1 : 0;
          const bListed = b.ebay_listing_url ? 1 : 0;
          if (bListed !== aListed) return bListed - aListed;
          // Then by completed_at desc
          const aDate = a.completed_at || "";
          const bDate = b.completed_at || "";
          return bDate.localeCompare(aDate);
        });
    },
    readyCount() {
      return this.filteredOrders.filter((o) => o.order_status === "COMPLETE").length;
    },
    listedCount() {
      return this.filteredOrders.filter((o) => !!o.ebay_listing_url).length;
    },
    totalListingValue() {
      const total = this.filteredOrders.reduce((sum, o) => {
        return sum + (parseFloat(o.sale_price) || 0);
      }, 0);
      return total.toFixed(2);
    },
  },
  methods: {
    async fetchOrders() {
      this.loading = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getOrders(accessToken);
        this.orders = Array.isArray(res.data) ? res.data : [];
        this.initShippingForms();
      } catch {
        this.error = "Unable to load orders.";
      } finally {
        this.loading = false;
      }
    },

    initShippingForms() {
      const forms = {};
      for (const o of this.orders) {
        forms[o.order_id] = {
          tracking_number: o.tracking_number || "",
          shipping_carrier: o.shipping_carrier || "",
          ship_date: o.ship_date || "",
          shipping_status: o.shipping_status || "",
        };
      }
      this.shippingForms = forms;
    },

    toggleExpand(orderId) {
      this.expandedId = this.expandedId === orderId ? null : orderId;
    },

    statusBadge(order) {
      const s = order.order_status;
      if (s === "COMPLETE") return "bg-success";
      if (s === "SOLD") return "bg-dark";
      return "bg-secondary";
    },

    openProcessSale(order) {
      this.saleModal.order = order;
      this.saleModal.visible = true;
    },

    async onSaleConfirm(ebayOrderId) {
      this.saleModal.visible = false;
      const order = this.saleModal.order;
      if (!ebayOrderId || !order) return;

      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const resp = await this.api.importEbayOrder(
          { order_id: order.order_id, ebay_order_id: ebayOrderId.trim() },
          accessToken
        );
        const data = resp.data;
        const parts = [];
        if (data.buyer_name) parts.push(`Buyer: ${data.buyer_name}`);
        if (data.sold_price) parts.push(`Price: $${data.sold_price}`);
        alert(`Sale processed! ${parts.join(", ")}`);
        await this.fetchOrders();
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.error = detail ? `eBay import failed: ${detail}` : "Unable to import eBay order.";
      }
    },

    openListingGenerator(order) {
      this.listingModal.order = order;
      this.listingModal.visible = true;
    },

    async saveShipping(orderId) {
      this.savingShippingId = orderId;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateOrderStatus(orderId, this.shippingForms[orderId], accessToken);
        await this.fetchOrders();
      } catch {
        this.error = "Unable to save shipping info.";
      } finally {
        this.savingShippingId = null;
      }
    },
  },
};
</script>
